/**
 * The page's side of the service worker: register it (production builds only), take a
 * waiting worker over on a launch-like resume (update.ts), reload once on a failed lazy
 * chunk, and prefetch tomorrow's daily in the evening. Never throws.
 */
import { localDateKey } from '@arrow/game-engine';

import { parseRoute } from '../app/router.tsx';
import { PRELOAD_RELOAD_KEY, shouldReloadAfterPreloadError, shouldTakeOver } from './update.ts';

let registration: ServiceWorkerRegistration | null = null;
let takingOver = false;

/** Ask the browser for a newer sw.js at most this often (it also checks on every navigation). */
const UPDATE_CHECK_MS = 60 * 60 * 1000;

export function registerServiceWorker(): void {
	if (!import.meta.env.PROD || typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
	listenForPreloadErrors();
	const start = () => {
		navigator.serviceWorker
			.register('/sw.js', { scope: '/', updateViaCache: 'none' })
			.then((reg) => {
				registration = reg;
				watchResumes(reg);
			})
			.catch(() => undefined);
	};
	if (document.readyState === 'complete') start();
	else window.addEventListener('load', start, { once: true });
}

/** The page is controlled by a worker (offline works). */
export function isControlled(): boolean {
	try {
		return typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller;
	} catch {
		return false;
	}
}

function watchResumes(reg: ServiceWorkerRegistration) {
	let hiddenAt: number | null = null;
	let hiddenDay: string | null = null;
	let lastCheck = Date.now();
	navigator.serviceWorker.addEventListener('controllerchange', () => {
		// Only the takeover this page asked for reloads it; another tab's never yanks a game away.
		if (takingOver) window.location.reload();
	});
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') {
			hiddenAt = Date.now();
			hiddenDay = localDateKey(new Date());
			return;
		}
		const now = Date.now();
		const decide = shouldTakeOver({
			hiddenAt,
			hiddenDay,
			now,
			today: localDateKey(new Date()),
			route: parseRoute(window.location.pathname).name,
			waiting: !!reg.waiting && !!navigator.serviceWorker.controller,
		});
		hiddenAt = null;
		if (decide && reg.waiting) {
			takingOver = true;
			reg.waiting.postMessage({ type: 'SKIP_WAITING' });
			return;
		}
		if (now - lastCheck > UPDATE_CHECK_MS) {
			lastCheck = now;
			void reg.update().catch(() => undefined);
		}
	});
}

function listenForPreloadErrors() {
	window.addEventListener('vite:preloadError', (event) => {
		try {
			const raw = window.sessionStorage.getItem(PRELOAD_RELOAD_KEY);
			const last = raw === null ? null : Number(raw);
			if (!shouldReloadAfterPreloadError(last, Date.now())) return;
			window.sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
			event.preventDefault();
			window.location.reload();
		} catch {
			// No sessionStorage: don't risk a loop; let the error surface.
		}
	});
}

/** Tests / dev panel: the current registration. */
export function swRegistration(): ServiceWorkerRegistration | null {
	return registration;
}
